import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { AdminPageHeader, Field, Toggle, Select, TextArea } from "./_helpers";
import { toast } from "sonner";
import { Mail, Send, FileText, Inbox, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

export default function AdminEmail() {
  const [tab, setTab] = useState("smtp");
  return (
    <div>
      <AdminPageHeader title="Mail Sunucusu & Şablonları" subtitle="SMTP yapılandırması ve düzenlenebilir mail şablonları" />
      <div className="flex gap-2 mb-4 flex-wrap">
        <TabBtn k="smtp" tab={tab} setTab={setTab} icon={Mail} label="SMTP" />
        <TabBtn k="templates" tab={tab} setTab={setTab} icon={FileText} label="Şablonlar" />
        <TabBtn k="outbox" tab={tab} setTab={setTab} icon={Inbox} label="Gönderim Logu" />
      </div>
      {tab === "smtp" && <SMTPPanel />}
      {tab === "templates" && <TemplatesPanel />}
      {tab === "outbox" && <OutboxPanel />}
    </div>
  );
}

const TabBtn = ({ k, tab, setTab, icon: Icon, label }) => (
  <button onClick={() => setTab(k)} className={`px-3 py-1.5 rounded text-sm flex items-center gap-1 ${tab === k ? "bg-[#e11d2a] text-white" : "border"}`}><Icon className="w-4 h-4" /> {label}</button>
);

function SMTPPanel() {
  const [cfg, setCfg] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  useEffect(() => { api.get("/smtp").then((r) => setCfg(r.data || {})); }, []);
  if (!cfg) return <div>Yükleniyor...</div>;
  const save = async () => { try { await api.put("/smtp", cfg); toast.success("Kaydedildi"); } catch { toast.error("Hata"); } };
  const sendTest = async () => {
    if (!testEmail) return toast.error("Test mail adresi girin");
    setTesting(true);
    try {
      const r = await api.post("/smtp/test", { to_email: testEmail });
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Test başarısız"); }
    finally { setTesting(false); }
  };
  return (
    <div className="bg-white border border-gray-100 rounded-lg p-5 space-y-3">
      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900 flex gap-2">
        <AlertTriangle className="w-5 h-5 shrink-0" />
        <div>Kendi mail hostunuzun bilgilerini buraya girebilirsiniz. SMTP devre dışı bırakılırsa veya host boş ise, sistem mailleri gönderim logunda <strong>skipped_no_smtp</strong> olarak kaydeder. Tüm tetiklenen mailler "Gönderim Logu" sekmesinde görünür.</div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Toggle label="SMTP Aktif" value={cfg.enabled} onChange={(v) => setCfg({ ...cfg, enabled: v })} />
        <div></div>
        <Field label="SMTP Host (ör. smtp.gmail.com)" value={cfg.host || ""} onChange={(e) => setCfg({ ...cfg, host: e.target.value })} />
        <Field label="Port (587 / 465 / 25)" type="number" value={cfg.port ?? 587} onChange={(e) => setCfg({ ...cfg, port: +e.target.value })} />
        <Field label="Kullanıcı Adı" value={cfg.username || ""} onChange={(e) => setCfg({ ...cfg, username: e.target.value })} />
        <Field label="Şifre" type="password" value={cfg.password || ""} onChange={(e) => setCfg({ ...cfg, password: e.target.value })} />
        <Field label="From Email (ör. info@sine81.com)" value={cfg.from_email || ""} onChange={(e) => setCfg({ ...cfg, from_email: e.target.value })} />
        <Field label="From İsim (ör. SİNE81)" value={cfg.from_name || ""} onChange={(e) => setCfg({ ...cfg, from_name: e.target.value })} />
        <Toggle label="STARTTLS (port 587 için)" value={cfg.use_tls} onChange={(v) => setCfg({ ...cfg, use_tls: v, use_ssl: v ? false : cfg.use_ssl })} />
        <Toggle label="SSL (port 465 için)" value={cfg.use_ssl} onChange={(v) => setCfg({ ...cfg, use_ssl: v, use_tls: v ? false : cfg.use_tls })} />
        <Toggle
          label="SSL sertifika doğrulamasını yap (shared hosting için kapatın)"
          value={cfg.verify_cert !== false}
          onChange={(v) => setCfg({ ...cfg, verify_cert: v })}
        />
      </div>
      <div className="flex gap-2 pt-2 border-t">
        <button onClick={save} className="px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold">Kaydet</button>
      </div>
      <div className="border-t pt-4 mt-4">
        <h4 className="font-semibold mb-2 flex items-center gap-1"><Send className="w-4 h-4" /> Test Maili Gönder</h4>
        <div className="flex gap-2">
          <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="ornek@email.com" className="flex-1 px-3 py-2 border border-gray-200 rounded text-sm" />
          <button onClick={sendTest} disabled={testing} className="px-4 py-2 bg-[#0b0b0d] text-white rounded text-sm">{testing ? "Gönderiliyor..." : "Test Gönder"}</button>
        </div>
      </div>
    </div>
  );
}

function TemplatesPanel() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = () => api.get("/email-templates").then((r) => { setItems(r.data); if (r.data.length && !selected) setSelected(r.data[0]); });
  useEffect(load, []);
  const save = async () => {
    try { await api.put(`/email-templates/${selected.key}`, selected); toast.success("Kaydedildi"); load(); }
    catch { toast.error("Hata"); }
  };
  return (
    <div className="grid lg:grid-cols-4 gap-4">
      <div className="bg-white border border-gray-100 rounded-lg p-3 lg:col-span-1">
        <h3 className="font-bold text-sm mb-2 text-gray-700">Şablonlar</h3>
        <div className="space-y-1">
          {items.map((t) => (
            <button key={t.key} onClick={() => setSelected(t)} className={`w-full text-left px-3 py-2 rounded text-sm ${selected?.key === t.key ? "bg-[#e11d2a] text-white" : "hover:bg-gray-50"}`}>
              <div className="font-semibold">{t.name}</div>
              <div className={`text-[10px] ${selected?.key === t.key ? "text-white/80" : "text-gray-500"}`}>{t.key}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="lg:col-span-3 bg-white border border-gray-100 rounded-lg p-5 space-y-3">
        {selected ? (
          <>
            <div className="flex items-center justify-between">
              <h3 className="font-bold">{selected.name}</h3>
              <Toggle label="Aktif" value={selected.is_active} onChange={(v) => setSelected({ ...selected, is_active: v })} />
            </div>
            <p className="text-sm text-gray-500">{selected.description}</p>
            {selected.variables?.length > 0 && (
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs font-bold mb-1 text-gray-700">Kullanılabilir Değişkenler (kullanım: <code>{`{{degisken_adi}}`}</code>)</div>
                <div className="flex flex-wrap gap-1">
                  {selected.variables.map((v) => <code key={v} className="text-xs bg-white border px-2 py-0.5 rounded">{`{{${v}}}`}</code>)}
                </div>
              </div>
            )}
            <Field label="Konu (Subject)" value={selected.subject || ""} onChange={(e) => setSelected({ ...selected, subject: e.target.value })} />
            <TextArea rows={4} label="Metin İçerik (Düz Metin)" value={selected.text_body || ""} onChange={(e) => setSelected({ ...selected, text_body: e.target.value })} />
            <TextArea rows={12} label="HTML İçerik" value={selected.html_body || ""} onChange={(e) => setSelected({ ...selected, html_body: e.target.value })} />
            <button onClick={save} className="px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold">Kaydet</button>
            <div className="border-t pt-4">
              <div className="text-sm font-bold mb-2">HTML Önizleme</div>
              <div className="border border-gray-200 rounded p-4 bg-white prose max-w-none" dangerouslySetInnerHTML={{ __html: selected.html_body || "" }}></div>
            </div>
          </>
        ) : <div>Şablon seçin</div>}
      </div>
    </div>
  );
}

function OutboxPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/email-outbox").then((r) => setItems(r.data)); }, []);
  return (
    <div className="bg-white border border-gray-100 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left">
          <tr><th className="px-3 py-2">Tarih</th><th className="px-3 py-2">Alıcı</th><th className="px-3 py-2">Şablon</th><th className="px-3 py-2">Konu</th><th className="px-3 py-2">Durum</th></tr>
        </thead>
        <tbody>
          {items.map((m) => (
            <tr key={m.id} className="border-t">
              <td className="px-3 py-2 text-xs">{new Date(m.created_at).toLocaleString("tr-TR")}</td>
              <td className="px-3 py-2 text-xs">{m.to}</td>
              <td className="px-3 py-2 text-xs">{m.template_key}</td>
              <td className="px-3 py-2 text-xs">{m.subject}</td>
              <td className="px-3 py-2 text-xs">
                {m.status === "sent" ? <span className="text-green-600 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Gönderildi</span>
                  : m.status === "failed" ? <span className="text-red-600 inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Başarısız</span>
                  : <span className="text-amber-600">{m.status}</span>}
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-gray-500">Henüz mail gönderimi yok</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
